import { getQuestObjectiveDisplayTarget } from "./questProgressHelpers";

function toFiniteNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

export function selectQuestJournalStatsEntries(entries = []) {
  return entries.filter((entry) => {
    if (!entry?.isActive) return true;
    return Boolean(entry?.allCompleted);
  });
}

function entryHasParticipation(entry) {
  return (entry?.objectives || []).some(
    (objective) => toFiniteNumber(objective?.progress?.current) > 0
  );
}

function objectiveCurrentValue(objective) {
  const target = getQuestObjectiveDisplayTarget(objective);
  if (objective?.status === "completed") return target;
  return Math.min(Math.max(0, Math.round(toFiniteNumber(objective?.progress?.current))), target);
}

function sumObjectivesByCategory(entries, categoryId) {
  let current = 0;
  let target = 0;

  for (const entry of entries) {
    for (const objective of entry?.objectives || []) {
      if (objective?.categoryId !== categoryId) continue;
      const objectiveTarget = getQuestObjectiveDisplayTarget(objective);
      target += objectiveTarget;
      current += objectiveCurrentValue(objective);
    }
  }

  return { current, target };
}

function sumReadingMinutes(entries) {
  let validated = 0;
  let renonced = 0;

  for (const entry of entries) {
    for (const objective of entry?.objectives || []) {
      if (objective?.categoryId !== "reading") continue;
      const minutes =
        toFiniteNumber(objective?.progress?.target) ||
        getQuestObjectiveDisplayTarget(objective);
      if (objective?.status === "completed") {
        validated += minutes;
      } else {
        renonced += minutes;
      }
    }
  }

  return { validated, renonced };
}

function addRewardItem(map, grant) {
  if (!grant || grant.kind === "relic") return;
  if (grant.kind !== "gem" && grant.kind !== "potion") return;

  const label = String(grant.label || grant.itemId || "").trim();
  if (!label) return;

  const quantity = Math.max(0, toFiniteNumber(grant.quantity, 1));
  if (quantity <= 0) return;

  map.set(label, (map.get(label) || 0) + quantity);
}

function addOfferedReward(gemsMap, potionsMap, grant) {
  if (!grant || grant.kind === "relic") return;
  if (grant.kind === "gem") addRewardItem(gemsMap, grant);
  if (grant.kind === "potion") addRewardItem(potionsMap, grant);
}

function mapToSortedEntries(map) {
  return Array.from(map.entries())
    .sort(([leftLabel], [rightLabel]) => leftLabel.localeCompare(rightLabel, "fr"))
    .map(([label, quantity]) => ({ label, quantity }));
}

export function formatQuestJournalReadingDuration(minutes) {
  const total = Math.max(0, Math.round(toFiniteNumber(minutes)));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${hours} H ${String(mins).padStart(2, "0")}`;
}

export function formatQuestJournalRatio(current, target) {
  const safeCurrent = Math.max(0, Math.round(toFiniteNumber(current)));
  const safeTarget = Math.max(0, Math.round(toFiniteNumber(target)));
  return `${safeCurrent.toLocaleString("fr-FR")}/${safeTarget.toLocaleString("fr-FR")}`;
}

function sumBreakdownQuantities(items = []) {
  return items.reduce((sum, item) => sum + Math.max(0, toFiniteNumber(item?.quantity)), 0);
}

export function formatQuestJournalCount(value) {
  return Math.max(0, Math.round(toFiniteNumber(value))).toLocaleString("fr-FR");
}

export function formatQuestJournalPercent(numerator, denominator) {
  const safeNumerator = toFiniteNumber(numerator);
  const safeDenominator = toFiniteNumber(denominator);
  if (safeDenominator <= 0) return "—";
  return `${Math.round((safeNumerator / safeDenominator) * 100)} %`;
}

export function computeQuestJournalStats(entries = []) {
  const statsEntries = selectQuestJournalStatsEntries(entries);
  const questCount = statsEntries.length;

  let successCount = 0;
  let participationCount = 0;
  let abandonmentCount = 0;
  let relicDiscoveredCount = 0;

  const instructorIds = new Set();
  const earnedGems = new Map();
  const earnedPotions = new Map();
  const offeredGems = new Map();
  const offeredPotions = new Map();

  let relicChanceMin = null;
  let relicChanceMax = null;

  for (const entry of statsEntries) {
    if (entry?.allCompleted) successCount += 1;

    if (entryHasParticipation(entry)) {
      participationCount += 1;
    } else {
      abandonmentCount += 1;
    }

    const characterId = entry?.dialogue?.characterId;
    if (characterId) instructorIds.add(characterId);

    if (entry?.rewards?.relicWon) relicDiscoveredCount += 1;

    const relicChance = entry?.rewards?.relicChancePercent;
    if (Number.isFinite(relicChance)) {
      relicChanceMin = relicChanceMin == null ? relicChance : Math.min(relicChanceMin, relicChance);
      relicChanceMax = relicChanceMax == null ? relicChance : Math.max(relicChanceMax, relicChance);
    }

    if (entry?.rewards?.primary) {
      addOfferedReward(offeredGems, offeredPotions, entry.rewards.primary);
    }
    if (entry?.rewards?.clover) {
      addOfferedReward(offeredGems, offeredPotions, entry.rewards.clover);
    }

    if (entry?.rewards?.status === "claimed" && Array.isArray(entry.rewards.grants)) {
      for (const grant of entry.rewards.grants) {
        addOfferedReward(earnedGems, earnedPotions, grant);
      }
    }
  }

  const reading = sumReadingMinutes(statsEntries);
  const readingImposed = reading.validated + reading.renonced;

  const capture = sumObjectivesByCategory(statsEntries, "capture");
  const memory = sumObjectivesByCategory(statsEntries, "memory-plays");
  const playGames = sumObjectivesByCategory(statsEntries, "play-games");

  const earnedGemsList = mapToSortedEntries(earnedGems);
  const earnedPotionsList = mapToSortedEntries(earnedPotions);

  return {
    questCount,
    successRate: formatQuestJournalPercent(successCount, questCount),
    participationRate: formatQuestJournalPercent(participationCount, questCount),
    abandonmentRate: formatQuestJournalPercent(abandonmentCount, questCount),
    readingValidatedLabel: formatQuestJournalReadingDuration(reading.validated),
    readingRenouncedLabel: formatQuestJournalReadingDuration(reading.renonced),
    readingFidelityRate: formatQuestJournalPercent(reading.validated, readingImposed),
    captureRatio: formatQuestJournalRatio(capture.current, capture.target),
    captureFidelityRate: formatQuestJournalPercent(capture.current, capture.target),
    memoryRatio: formatQuestJournalRatio(memory.current, memory.target),
    memoryFidelityRate: formatQuestJournalPercent(memory.current, memory.target),
    playGamesRatio: formatQuestJournalRatio(playGames.current, playGames.target),
    playGamesFidelityRate: formatQuestJournalPercent(playGames.current, playGames.target),
    instructorsCountLabel: formatQuestJournalCount(instructorIds.size),
    relicsCountLabel: formatQuestJournalCount(relicDiscoveredCount),
    earnedGems: earnedGemsList,
    earnedGemsTotal: sumBreakdownQuantities(earnedGemsList),
    earnedPotions: earnedPotionsList,
    earnedPotionsTotal: sumBreakdownQuantities(earnedPotionsList),
    offeredGems: mapToSortedEntries(offeredGems),
    offeredPotions: mapToSortedEntries(offeredPotions),
    relicChanceMinLabel:
      relicChanceMin == null ? "—" : `${Math.round(relicChanceMin)} %`,
    relicChanceMaxLabel:
      relicChanceMax == null ? "—" : `${Math.round(relicChanceMax)} %`,
  };
}
